import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useUserListingAccess } from "@/hooks/use-user-listing-access";
import { useUserRole } from "@/hooks/use-user-role";
import { fetchAccessibleProperties, fetchAccessiblePropertiesByPropertyIds, type CrmSource, type OwnerProperty } from "@/lib/owner/crm";
import { mockPeriods, type PeriodKey } from "@/lib/owner/mock";
import {
  readSharedPropertyIds,
  writeSharedClientCodes,
  writeSharedPropertyIds,
  useSharedClientCodes,
  useSharedPropertyIds,
} from "@/lib/shared-client-filter";

interface OwnerFilterState {
  // Owner filter (admin/super_admin/am only)
  clientCodes: string[];
  setClientCodes: (codes: string[]) => void;
  ownerFilterVisible: boolean;
  availableClients: { code: string; source: CrmSource }[];
  needsClientSelection: boolean;

  // Property filter
  selectedProperties: string[];                // property_ids
  setSelectedProperties: (p: string[]) => void;
  availableProperties: OwnerProperty[];        // narrowed by clientCodes
  allAccessibleProperties: OwnerProperty[];    // unfiltered base list
  propertyLookup: Map<string, OwnerProperty>;
  sourceMap: Map<string, CrmSource>;           // property_id → source

  // Period
  period: PeriodKey;
  setPeriod: (p: PeriodKey) => void;
  customRange: { fromMonth: string; toMonth: string }; // "YYYY-MM"
  setCustomRange: (r: { fromMonth: string; toMonth: string }) => void;

  loading: boolean;
}

const Ctx = createContext<OwnerFilterState | null>(null);

// Helper: pick the "listed-only" subset; fall back to all if none flagged listed.
function listedOnly(props: OwnerProperty[]): string[] {
  const listed = props.filter((p) => p.push_enabled === true).map((p) => p.property_id);
  return listed.length > 0 ? listed : props.map((p) => p.property_id);
}

export function OwnerFilterProvider({ children }: { children: ReactNode }) {
  const { user, crmTokenReady } = useAuth();
  const { allowedClientCodes, allowedPropertyIds, defaultClientSource, defaultListingSource, isReady: aclReady } = useUserListingAccess();
  const { primaryRole, isAdmin, isLoading: roleLoading } = useUserRole();

  const isOwner = primaryRole === "owner" || primaryRole === "owner_test";
  // Owner picks from their per-listing ACL (user_listing_access), not from
  // owner codes — so the client/owner-code pill is hidden for owners too.
  const ownerFilterVisible = primaryRole !== "client" && primaryRole !== "client_test" && !isOwner;
  // Admin and super_admin see every owner on the Owner Dashboard, regardless of user_owner_access rows.
  const effectiveAllowedClientCodes: Set<string> | null = isAdmin ? null : allowedClientCodes;

  // ── Period + customRange (per-context, not shared cross-page) ─────────────
  const SS_KEY = "ownerFilters:v1";
  const persisted = (() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.sessionStorage.getItem(SS_KEY);
      return raw ? JSON.parse(raw) as {
        period?: PeriodKey;
        customRange?: { fromMonth: string; toMonth: string };
      } : null;
    } catch { return null; }
  })();

  const [period, setPeriod] = useState<PeriodKey>(persisted?.period ?? "last_month");
  const [customRange, setCustomRange] = useState<{ fromMonth: string; toMonth: string }>(() => {
    if (persisted?.customRange) return persisted.customRange;
    const today = new Date().toISOString().slice(0, 7); // YYYY-MM (UTC)
    return { fromMonth: today, toMonth: today };
  });

  // ── Selection state: DERIVED from the shared external store ───────────────
  // Single source of truth. No mirroring into useState, no sync effects, no
  // writeback effects. The store is the state; we only read it (for renders)
  // and write to it (for user actions + seeding).
  const sharedClient = useSharedClientCodes();
  const sharedProps = useSharedPropertyIds();

  const clientCodes = useMemo<string[]>(
    () => (ownerFilterVisible ? (sharedClient ?? []) : []),
    [sharedClient, ownerFilterVisible],
  );

  const enabled = !!user && crmTokenReady && aclReady && !roleLoading && primaryRole !== null;

  const propertyIdsKey = allowedPropertyIds === null
    ? "__ALL__"
    : Array.from(allowedPropertyIds).sort().join(",");
  const clientCodesKey = effectiveAllowedClientCodes === null
    ? "__ALL__"
    : Array.from(effectiveAllowedClientCodes).sort().join(",");

  const { data: allAccessibleProperties = [], isLoading } = useQuery({
    queryKey: isOwner
      ? ["owner_accessible_properties", "by_property_id", propertyIdsKey, defaultListingSource ?? null]
      : ["owner_accessible_properties", "by_client_code", clientCodesKey, defaultClientSource ?? null],
    queryFn: () => isOwner
      ? fetchAccessiblePropertiesByPropertyIds(allowedPropertyIds ?? null, defaultListingSource)
      : fetchAccessibleProperties(effectiveAllowedClientCodes ?? null, defaultClientSource),
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });



  // Owner list derived from accessible properties
  const availableClients = useMemo(() => {
    const m = new Map<string, CrmSource>();
    for (const p of allAccessibleProperties) m.set(p.client_code, p.source);
    return Array.from(m.entries())
      .map(([code, source]) => ({ code, source }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [allAccessibleProperties]);

  // When admin/AM has no client selected, treat as "no selection" — not "all".
  const needsClientSelection = ownerFilterVisible && clientCodes.length === 0;

  // Properties narrowed by selected owners (admin only).
  const availableProperties = useMemo(() => {
    if (needsClientSelection) return [];
    if (!ownerFilterVisible) return allAccessibleProperties;
    const set = new Set(clientCodes);
    return allAccessibleProperties.filter((p) => set.has(p.client_code));
  }, [allAccessibleProperties, clientCodes, ownerFilterVisible, needsClientSelection]);

  // selectedProperties is DERIVED — filter the shared store down to IDs that
  // belong to this provider's accessible universe. Foreign IDs (from other
  // pages with broader scope) remain in the shared store but are hidden here.
  const selectedProperties = useMemo<string[]>(() => {
    if (sharedProps === null) return [];
    if (allAccessibleProperties.length === 0) return []; // pre-load: nothing accessible yet
    const allowed = new Set(allAccessibleProperties.map((p) => p.property_id));
    return sharedProps.filter((id) => allowed.has(id));
  }, [sharedProps, allAccessibleProperties]);

  const propertyLookup = useMemo(() => {
    const m = new Map<string, OwnerProperty>();
    for (const p of allAccessibleProperties) m.set(p.property_id, p);
    return m;
  }, [allAccessibleProperties]);

  const sourceMap = useMemo(() => {
    const m = new Map<string, CrmSource>();
    for (const p of allAccessibleProperties) m.set(p.property_id, p.source);
    return m;
  }, [allAccessibleProperties]);

  // ── Writers: explicit user actions write straight to the shared store ─────
  // When admin/AM picks client(s), auto-reset property selection to the listed
  // (push_enabled=true) subset for those clients, and flip the Property pill's
  // status segmented control to "listed".
  const pendingSeedRef = useRef<string[] | null>(null);
  const setClientCodes = useCallback((rawCodes: string[]) => {
    const codes = rawCodes.map((c) => c.toUpperCase());
    writeSharedClientCodes(codes);
    if (!ownerFilterVisible) return;

    if (codes.length === 0) {
      writeSharedPropertyIds([]);
      pendingSeedRef.current = null;
      return;
    }

    const codeSet = new Set(codes);
    const scoped = allAccessibleProperties.filter((p) => codeSet.has(p.client_code));
    if (scoped.length === 0) {
      // Properties may still be loading — defer the seed to the effect below.
      pendingSeedRef.current = codes;
    } else {
      writeSharedPropertyIds(listedOnly(scoped));
      pendingSeedRef.current = null;
    }

    if (typeof window !== "undefined") {
      try {
        window.sessionStorage.setItem("ownerFilters:propertyStatus:v1", "listed");
        window.dispatchEvent(new Event("ownerFilters:propertyStatus"));
      } catch { /* ignore */ }
    }
  }, [ownerFilterVisible, allAccessibleProperties]);

  // If setClientCodes fired before properties loaded, complete the seed once
  // they arrive.
  useEffect(() => {
    const pending = pendingSeedRef.current;
    if (!pending || allAccessibleProperties.length === 0) return;
    const codeSet = new Set(pending);
    const scoped = allAccessibleProperties.filter((p) => codeSet.has(p.client_code));
    if (scoped.length > 0) {
      writeSharedPropertyIds(listedOnly(scoped));
      pendingSeedRef.current = null;
    }
  }, [allAccessibleProperties]);

  // Tracks whether the client/client_test user has manually changed the
  // property selection during this session. Once true, the auto-seed effect
  // below stops overriding their choice.
  const userTouchedRef = useRef(false);

  // Reset the "user touched" flag when the user or role changes (new login).
  useEffect(() => {
    userTouchedRef.current = false;
  }, [user?.id, primaryRole]);

  const setSelectedProperties = useCallback((props: string[]) => {
    // Preserve foreign IDs (visible only on other pages with broader scope)
    // by merging them with the caller's selection. Done at the writer so
    // there's no downstream effect re-reading its own output.
    const known = new Set<string>();
    for (const p of allAccessibleProperties) known.add(p.property_id);
    const prev = readSharedPropertyIds() ?? [];
    const foreign = prev.filter((id) => !known.has(id));
    userTouchedRef.current = true;
    writeSharedPropertyIds(Array.from(new Set([...foreign, ...props])));
  }, [allAccessibleProperties]);

  // ── Single seeding effect ─────────────────────────────────────────────────
  // Admin / AM: seed listed-only ONLY when the shared store is fully absent;
  //   user-managed selections persist across pages and navigations.
  // Client / client_test: ACL-locked roles. We own their default selection
  //   and (re)seed listed-only whenever their visible selection is empty,
  //   until they manually toggle something (userTouchedRef).
  const accessibleSig = useMemo(
    () => allAccessibleProperties.map((p) => p.property_id).sort().join(","),
    [allAccessibleProperties],
  );
  const seedKey = useMemo(() => JSON.stringify({
    userId: user?.id ?? null,
    role: primaryRole,
    clientCodes: ownerFilterVisible ? [...clientCodes].sort() : null,
    accessibleSig: ownerFilterVisible ? null : accessibleSig,
  }), [user?.id, primaryRole, clientCodes, ownerFilterVisible, accessibleSig]);
  const lastSeedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (allAccessibleProperties.length === 0) return;

    if (!ownerFilterVisible) {
      // Client / client_test — re-seed listed-only whenever the visible
      // selection is empty and the user hasn't manually changed anything
      // this session. Robust to late scope switches and transient empty
      // `availableProperties` states during login.
      if (availableProperties.length === 0) return;
      if (userTouchedRef.current) return;
      if (selectedProperties.length > 0) return;

      writeSharedPropertyIds(listedOnly(availableProperties));
      if (typeof window !== "undefined") {
        try {
          window.sessionStorage.setItem("ownerFilters:propertyStatus:v1", "listed");
          window.dispatchEvent(new Event("ownerFilters:propertyStatus"));
        } catch { /* ignore */ }
      }
      return;
    }

    // Admin / AM — preserve any existing user selection across pages.
    if (lastSeedKeyRef.current === seedKey) return;
    lastSeedKeyRef.current = seedKey;
    const current = readSharedPropertyIds();
    const shouldSeed = current === null && clientCodes.length > 0 && availableProperties.length > 0;
    if (shouldSeed) {
      writeSharedPropertyIds(listedOnly(availableProperties));
    }
  }, [seedKey, enabled, allAccessibleProperties, availableProperties, ownerFilterVisible, clientCodes, selectedProperties]);

  // Force-clear shared client codes for client role (defensive — should never
  // be set for them, but if a prior admin session stamped a value into a
  // shared scope we'd want it cleared).
  useEffect(() => {
    if (!ownerFilterVisible && sharedClient !== null && sharedClient.length > 0) {
      writeSharedClientCodes([]);
    }
  }, [ownerFilterVisible, sharedClient]);

  // Persist period + customRange across navigations.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(SS_KEY, JSON.stringify({ period, customRange }));
    } catch { /* ignore quota / private mode */ }
  }, [period, customRange]);

  const value = useMemo<OwnerFilterState>(() => ({
    clientCodes,
    setClientCodes,
    ownerFilterVisible,
    availableClients,
    needsClientSelection,
    selectedProperties,
    setSelectedProperties,
    availableProperties,
    allAccessibleProperties,
    propertyLookup,
    sourceMap,
    period,
    setPeriod,
    customRange,
    setCustomRange,
    loading: isLoading || !enabled,
  }), [
    clientCodes, setClientCodes, ownerFilterVisible, availableClients, needsClientSelection,
    selectedProperties, setSelectedProperties, availableProperties, allAccessibleProperties,
    propertyLookup, sourceMap, period, customRange, isLoading, enabled,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOwnerFilters() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useOwnerFilters must be used within OwnerFilterProvider");
  return v;
}

/**
 * Static provider used by the public snapshot viewer.
 *
 * The shared link locks the recipient to the property subset that the
 * sharer selected at share time — but inside that subset they can still
 * toggle properties on/off and watch KPIs / charts recompute at portfolio
 * level. The `availableProperties` universe IS the captured set, so
 * deselecting and reselecting cannot reach anything outside it.
 *
 * Period / customRange remain frozen because the captured raw history
 * corresponds to a fixed range.
 */
export function StaticOwnerFilterProvider({
  children,
  filters,
  properties,
  sourceMapEntries,
}: {
  children: ReactNode;
  filters?: { clientCodes?: string[]; selectedProperties?: string[]; period?: PeriodKey; customRange?: { fromMonth: string; toMonth: string } };
  properties?: OwnerProperty[];
  sourceMapEntries?: [string, CrmSource][];
}) {
  const available = properties ?? [];
  const sourceMap = useMemo(
    () => new Map<string, CrmSource>(sourceMapEntries ?? []),
    [sourceMapEntries],
  );
  const lookup = useMemo(() => {
    const m = new Map<string, OwnerProperty>();
    for (const p of available) m.set(p.property_id, p);
    return m;
  }, [available]);

  // Default selection = everything that was shared (or whatever the sharer
  // had selected at capture time, intersected with `available` so foreign
  // ids never leak in).
  const allowedIds = useMemo(
    () => new Set(available.map((p) => p.property_id)),
    [available],
  );
  const initialSelection = useMemo(() => {
    const fromFilters = (filters?.selectedProperties ?? []).filter((id) => allowedIds.has(id));
    if (fromFilters.length > 0) return fromFilters;
    return available.map((p) => p.property_id);
  }, [filters?.selectedProperties, available, allowedIds]);

  const [selectedProperties, setSelectedProperties] = useState<string[]>(initialSelection);
  const lastInitRef = useRef<string>("");
  useEffect(() => {
    const sig = initialSelection.join(",");
    if (lastInitRef.current === sig) return;
    lastInitRef.current = sig;
    setSelectedProperties(initialSelection);
  }, [initialSelection]);

  const value: OwnerFilterState = {
    clientCodes: filters?.clientCodes ?? [],
    setClientCodes: () => {},
    ownerFilterVisible: false,
    availableClients: [],
    needsClientSelection: false,
    selectedProperties,
    setSelectedProperties: (next) => {
      // Keep selection strictly within the captured universe.
      setSelectedProperties(next.filter((id) => allowedIds.has(id)));
    },
    availableProperties: available,
    allAccessibleProperties: available,
    propertyLookup: lookup,
    sourceMap,
    period: filters?.period ?? "last_month",
    setPeriod: () => {},
    customRange: filters?.customRange ?? { fromMonth: new Date().toISOString().slice(0, 7), toMonth: new Date().toISOString().slice(0, 7) },
    setCustomRange: () => {},
    loading: false,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}


export { mockPeriods };
